import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { api } from '../../lib/api'
import { downloadBlob } from '../../lib/download'

interface JournalRental {
  id: string
  rentalNumber: string
  bike: string
  customerPhone: string
  customerName: string
  documentNumber: string
  manager: string
  pickupPoint: string
  returnPoint: string
  startAt: string
  endAt: string | null
  pricingMode: string
  basePrice: number
  totalPrice: number
  prepaymentAmount: number
  totalPaid: number
  totalRefunded: number
  balance: number
  actualPaid: number
  paymentMethod: string
}

const PRICING_LABELS: Record<string, string> = { hourly: '⏱ почасовая', daily: '🌙 суточная', override: '✏️ ручная' }
const fmt = (n: number) => n.toLocaleString('ru-RU') + ' ₽'
const fmtDate = (s: string) => {
  const d = new Date(s)
  return d.toLocaleDateString('ru', { day: '2-digit', month: '2-digit' }) + ' ' + d.toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' })
}

export default function JournalPage() {
  const today = new Date()
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
  const [from, setFrom] = useState(firstOfMonth.toISOString().slice(0, 10))
  const [to, setTo] = useState(today.toISOString().slice(0, 10))

  const { data, isLoading } = useQuery({
    queryKey: ['journal', from, to],
    queryFn: () => api.get(`/payments/journal?from=${from}&to=${to}T23:59:59`).then((r) => r.data),
  })

  const rentals: JournalRental[] = data?.rentals ?? []
  const summary = data?.summary as
    | {
        count: number; shown: number
        actualRevenue: number; avgDurationMinutes: number; refundCount: number
        modelLeader: string; modelLoser: string
      }
    | undefined

  const handleCsvExport = async () => {
    const res = await api.get(`/payments/journal/csv?from=${from}&to=${to}T23:59:59`, { responseType: 'blob' })
    await downloadBlob(res.data, `journal-${from}-${to}.csv`)
  }

  // Owner-friendly metrics come from the server summary (whole period).
  const periodCount = summary?.count ?? rentals.length
  const shownCount = summary?.shown ?? rentals.length
  const isCapped = periodCount > shownCount
  const actualRevenue = summary?.actualRevenue ?? rentals.reduce((s, r) => s + r.actualPaid, 0)
  const refundCount = summary?.refundCount ?? 0
  const modelLeader = summary?.modelLeader ?? '—'
  const modelLoser = summary?.modelLoser ?? '—'
  const avgDurationMinutes = summary?.avgDurationMinutes ?? 0

  const formatAvgDuration = (minutes: number): string => {
    if (minutes < 1) return '—'
    if (minutes < 60) return `${Math.round(minutes)} мин`
    const h = Math.floor(minutes / 60); const m = Math.round(minutes % 60)
    return m > 0 ? `${h} ч ${m} мин` : `${h} ч`
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Журнал аренд</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {periodCount} аренд за период
            {isCapped && <span className="text-amber-600"> · в таблице показаны первые {shownCount}, итоги — за весь период</span>}
          </p>
        </div>
        <button
          onClick={handleCsvExport}
          className="flex items-center gap-2 border border-gray-300 px-4 py-2 rounded-xl text-sm hover:bg-gray-50"
        >
          📥 CSV экспорт
        </button>
      </div>

      {/* Date filter */}
      <div className="bg-white rounded-2xl border border-gray-200 p-4 flex flex-wrap gap-4 items-end">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">С</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
            className="border border-gray-300 rounded-xl px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">По</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
            className="border border-gray-300 rounded-xl px-3 py-2 text-sm" />
        </div>
      </div>

      {/* Totals — owner-friendly metrics (#5.3) */}
      {periodCount > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {[
            ['Кол-во аренд', String(periodCount), false],
            ['Ср. время аренды', formatAvgDuration(avgDurationMinutes), false],
            ['Выручка (факт)', fmt(actualRevenue), false],
            ['Возвратов (проблемных)', `${refundCount} шт`, false],
            ['Лидер по арендам', modelLeader, true],
            ['Аутсайдер по арендам', modelLoser, true],
          ].map(([label, val, small]) => (
            <div key={String(label)} className="bg-white border border-gray-200 rounded-2xl p-4">
              <p className="text-xs text-gray-500">{label}</p>
              <p className={`font-bold text-gray-900 mt-1 ${small ? 'text-sm break-words' : 'text-xl'}`}>{val}</p>
            </div>
          ))}
        </div>
      )}

      {isLoading && <p className="text-gray-400 text-center py-12">Загрузка...</p>}

      {!isLoading && rentals.length === 0 && (
        <div className="text-center py-16 text-gray-400">
          <div className="text-5xl mb-3">📋</div>
          <p>Нет аренд за выбранный период</p>
        </div>
      )}

      {rentals.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50 text-xs text-gray-500 uppercase">
                  <th className="px-4 py-3 text-left">№</th>
                  <th className="px-4 py-3 text-left">Велосипед</th>
                  <th className="px-4 py-3 text-left">Клиент</th>
                  <th className="px-4 py-3 text-left">Менеджер</th>
                  <th className="px-4 py-3 text-left">Точка</th>
                  <th className="px-4 py-3 text-left">Начало</th>
                  <th className="px-4 py-3 text-left">Конец</th>
                  <th className="px-4 py-3 text-left">Тариф</th>
                  <th
                    className="px-4 py-3 text-right normal-case"
                    title="Фактически полученные деньги по аренде (предоплаты + доплаты − возвраты)"
                  >
                    <span className="block uppercase text-xs text-gray-500">Сумма</span>
                    <span className="block text-[10px] font-normal text-gray-400 mt-0.5">по факту оплат</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {rentals.map((r) => {
                  const isActive = !r.endAt
                  return (
                    <tr key={r.id} className={`hover:bg-gray-50 transition-colors ${isActive ? 'bg-blue-50/40' : ''}`}>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap font-mono">{r.rentalNumber || '—'}</td>
                      <td className="px-4 py-3 font-medium">
                        {r.bike}
                        {isActive && <span className="ml-1 text-[10px] text-blue-500 font-normal">активна</span>}
                      </td>
                      <td className="px-4 py-3">
                        {r.customerName ? (
                          <>
                            <p>{r.customerName}</p>
                            <p className="text-xs text-gray-400">{r.customerPhone}</p>
                          </>
                        ) : (
                          <p>{r.customerPhone}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{r.manager}</td>
                      <td className="px-4 py-3 text-gray-600">
                        <p className="text-xs">{r.pickupPoint}</p>
                        {r.returnPoint !== r.pickupPoint && <p className="text-xs text-orange-500">→ {r.returnPoint}</p>}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{fmtDate(r.startAt)}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{r.endAt ? fmtDate(r.endAt) : '—'}</td>
                      <td className="px-4 py-3">
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                          {PRICING_LABELS[r.pricingMode] ?? r.pricingMode}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-gray-900">{fmt(r.actualPaid)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
